import { IZendeskTicket } from "@kuika/kuika-cl-model/src/models/zendesk";
import { ReactNode } from "react";
import ZendeskService from "../../../services/zendesk-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { TicketDetailSkeleton } from "./helpdesk-skeleton";
import NewTicket from "./new-ticket";
import NoTicket from "./no-ticket";
import { TicketViewer } from "./ticket-viewer";
import "./zendesk-canvas.scss";
import { buildRequesterNameMap } from "./zendesk-ticket-filter";

export interface IZendeskCanvasState {
  isSignedIn: boolean;
  zendeskUserId: number;
}

export interface IZendeskCanvasProps {}

class Zendesk extends PureSdComponent<IZendeskCanvasProps & IWithSDProps & IWithContextProp, IZendeskCanvasState> {
  constructor(props: IZendeskCanvasProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isSignedIn: false,
      zendeskUserId: 0
    };
  }

  componentDidMount(): void {
    // Selection and the composer are decided once per OPEN by `openHelpdesk` — called from the
    // Helpdesk modal's mount and from entering the full-page view, never from here. This mount
    // ALSO fires on every Board→Inbox tab switch inside an already-open modal (Inbox and Board
    // are separate render branches, so switching between them remounts this component), and
    // resetting selection on that path would overwrite whatever ticket the board click just
    // chose. So mounting only refreshes the list.
    this.getTickets(false);
  }

  private getTickets = async (shouldSelect: boolean) => {
    try {
      this.props.sd.setTicketsLoading(true);

      if (!this.props.context?.userInfo) return;

      const res = await ZendeskService.GetTickets({
        userMail: this.props.context?.userInfo.username
      });

      if (res.status !== 200) {
        return;
      }

      const ticketArray: IZendeskTicket[] = res.data.groupTicketResponse.tickets.filter(
        (t: IZendeskTicket) => t.via.channel !== "native_messaging"
      );

      if (ticketArray.length === 0) return;

      const sortedArray: IZendeskTicket[] = ticketArray.sort((a: IZendeskTicket, b: IZendeskTicket) =>
        b.updatedAt
          .toString()
          .split("/")
          .reverse()
          .join()
          .localeCompare(a.updatedAt.toString().split("/").reverse().join())
      );
      this.props.sd.setTicketList(sortedArray);
      this.props.sd.setRequesterNames(buildRequesterNameMap(res.data.requesters ?? []));
      this.setState({ zendeskUserId: res.data.userZendeskId });
      // The fetch is slower than the user. If they hit New ticket while it was in flight,
      // opening the newest ticket now would pull the composer out from under them —
      // `selectTicket` clears the creating flag as part of its own work.
      if (shouldSelect && !this.props.sd.isZendeskTicketCreating) {
        this.props.sd.selectTicket(sortedArray[0].id);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.props.sd.setTicketsLoading(false);
    }
  };

  private onTicketSent = () => {
    this.props.sd.onZendeskTicketCreate(false);
    this.props.sd.onZendeskTicketSelect(null);
    this.props.sd.setTempHelpdeskNewTicketValues({
      subject: "",
      message: ""
    });
    this.getTickets(true);
  };

  /**
   * The three states are mutually exclusive by construction, not by the callers happening to
   * clear each other's flags. Two of them own an `input[type="file"]`, and the E2E suite
   * addresses the reply upload as the page's only file input — an overlap would break it, and
   * `selectTicket` does clear its two flags in separate setState calls.
   */
  private renderState = (): ReactNode => {
    // A ticket switch keeps the OUTGOING ticket (or the composer) on screen for the whole
    // fetch, so this has to win over BOTH branches below it. Against the ticket viewer:
    // dimming the last ticket's thread under a spinner is what made a row click read as
    // "nothing happened" for the second the detail was in flight. Against the composer: the
    // composer is now the default right pane on every open, `isZendeskTicketCreating` only
    // clears once `selectTicket`'s fetch resolves, and without this ordering a row click while
    // the composer is showing would leave it on screen for the whole fetch and then jump
    // straight to the ticket with no loading feedback at all.
    if (this.props.sd.isTicketDetailLoading) {
      return <TicketDetailSkeleton />;
    }

    if (this.props.sd.isZendeskTicketCreating) {
      return (
        <NewTicket sd={this.props.sd} onTicketSent={this.onTicketSent} profileInfo={this.props.context?.userInfo} />
      );
    }

    if (this.props.sd.selectedTicket) {
      return (
        <TicketViewer
          key={`zendesk-ticket-${this.props.sd.selectedTicket.id}-${this.props.sd.selectedTicket.createdAt}`}
          ticket={this.props.sd.selectedTicket}
          isTicketRequester={this.props.sd.selectedTicket.requesterId === this.state.zendeskUserId}
        />
      );
    }

    // Right after sending a ticket: the list refetch is still in flight and will select the
    // ticket that was just created into this very area, so the placeholder is the ticket's
    // shape rather than the list's. The composer is closed by then (`onTicketSent`), which is
    // what makes this branch reachable at all — on a fresh open the branch above always wins.
    if (this.props.sd.isTicketsLoading) return <TicketDetailSkeleton />;

    // Only when there is genuinely nothing to show. A ticket that failed to open — the
    // not-ready path in `selectTicket` leaves `selectedTicket` null — must not turn a full
    // list into "you don't have any tickets".
    if (this.props.sd.zendeskTicketList.length > 0) return null;

    return <NoTicket onClickCreate={this.props.sd.onZendeskTicketCreate} />;
  };

  render() {
    return (
      <div className="kuika-helpdesk-canvas">
        <div className="kuika-helpdesk-canvas__content">{this.renderState()}</div>
      </div>
    );
  }

  /**
   * Renders on any change that reaches it. The panel's own writes already forced a render —
   * they carry `OtherSetStates`, which `shouldComponentUpdate` allows above this method — so
   * what `true` actually buys is the off-allowlist reasons (`Loading`, and the ones the
   * surrounding designer sets while the panel is open), where stale ticket state would show.
   */
  checkNeedRender = (): boolean => {
    return true;
  };
}

const zendeskCanvas = withContext<IZendeskCanvasProps>(withSD<IZendeskCanvasProps>(Zendesk));
export { zendeskCanvas as ZendeskCanvas };
